import Link from 'next/link';
import { PartyPopper } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { NAV_ICONS } from '../components/nav-icons';
import { useSidebarNav, type NavId, type SidebarNavItem } from '../hooks/use-sidebar-nav';
import { RailTooltip } from './RailTooltip';
import styles from './RailNav.module.css';

// The rail's destinations in the design's order (Figma `Sidebar-Menu` 22535:282583): Home, Agents,
// Calendar, Wins. Which of them exist and what a click does is useSidebarNav's, so the gates (Calendar's
// release, Wins only once there is one) are today's.
// Chats is the header row above (RailChats) and Connect WhatsApp is the card in the foot.
const ORDER: NavId[] = ['feed', 'agents', 'calendar', 'wins'];

// Figma's Wins glyph is the DS "Celebrate" party popper, not the trophy the old rail draws.
const ICONS: Partial<Record<NavId, React.ReactNode>> = { ...NAV_ICONS, wins: <PartyPopper size={16} aria-hidden /> };

export function RailNav({ current, collapsed = false }: { current?: NavId; collapsed?: boolean }) {
  const { t } = useTranslation();
  const { items } = useSidebarNav({ current });
  const rows = ORDER.map((id) => items.find((item) => item.id === id)).filter((item): item is SidebarNavItem => !!item);

  return (
    <nav aria-label={t('nav.primary')} className={collapsed ? styles.navCollapsed : styles.nav}>
      {rows.map((item) =>
        collapsed ? (
          <RailTooltip key={item.id} label={item.label}>
            <Link
              href={item.route}
              as={item.path}
              onClick={item.onClick}
              aria-label={item.label}
              aria-current={item.isActive ? 'page' : undefined}
              className={`orion-nav-item ${styles.iconItem}`}
            >
              {ICONS[item.id]}
            </Link>
          </RailTooltip>
        ) : (
          <Link
            key={item.id}
            href={item.route}
            as={item.path}
            onClick={item.onClick}
            aria-current={item.isActive ? 'page' : undefined}
            className={`orion-nav-item ${styles.item}`}
          >
            <span aria-hidden className={styles.icon}>
              {ICONS[item.id]}
            </span>
            <span className={styles.label}>{item.label}</span>
          </Link>
        ),
      )}
    </nav>
  );
}
